import { FBLive } from '@/types/fb-type';

/** Extract Facebook object id from page_post or raw id (last `_` segment). */
export function objectIdFromFbId(id: string) {
  if (!id.includes('_')) return id;
  const parts = id.split('_');
  return parts[parts.length - 1] || id;
}

/** Parse API times like ISO or `yyyy-MM-dd HH:mm:ss`. */
export function parseLiveTimeMs(raw: string | null | undefined): number {
  if (!raw) return NaN;
  const direct = Date.parse(raw);
  if (!Number.isNaN(direct)) return direct;
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const parsed = Date.parse(normalized);
  if (!Number.isNaN(parsed)) return parsed;
  return Date.parse(`${normalized}Z`);
}

export function isCurrentlyLiveStatus(status: string | null | undefined) {
  if (!status) return false;
  const normalized = status.toLowerCase();
  return normalized === 'live' || normalized === 'live_now';
}

/**
 * Stale cache rows often keep status=LIVE for old streams.
 * Also demote empty LIVE rows older than a few hours (common junk from /fb/live).
 */
export function effectiveLiveStatus(
  live: Pick<FBLive, 'status' | 'creationTime' | 'commentCount' | 'liveViews'>,
) {
  if (!isCurrentlyLiveStatus(live.status)) return live.status || 'VOD';

  const created = parseLiveTimeMs(live.creationTime);
  const emptyStats = (live.liveViews == null || live.liveViews === 0) && (live.commentCount ?? 0) === 0;
  // Without a parseable start time we cannot prove staleness — keep explicit LIVE (socket/API).
  if (Number.isNaN(created)) return live.status;

  const ageMs = Date.now() - created;
  const hourMs = 60 * 60 * 1000;
  if (ageMs > 24 * hourMs) return 'VOD';
  if (emptyStats && ageMs > 6 * hourMs) return 'VOD';
  return live.status;
}

export function isFreshLive(live: Pick<FBLive, 'status' | 'creationTime' | 'commentCount' | 'liveViews'>) {
  return isCurrentlyLiveStatus(effectiveLiveStatus(live));
}

export function liveMatchesPostId(live: FBLive, postId: string) {
  if (!postId) return false;
  const objectId = objectIdFromFbId(postId);
  return (
    live.id === objectId ||
    live.id === postId ||
    live.postId === postId ||
    (!!live.postId && live.postId.endsWith(objectId))
  );
}

export function sortLivesForSidebar(lives: FBLive[]) {
  return [...lives].sort((a, b) => {
    const aLive = isFreshLive(a) ? 0 : 1;
    const bLive = isFreshLive(b) ? 0 : 1;
    if (aLive !== bLive) return aLive - bLive;
    return parseLiveTimeMs(b.creationTime) - parseLiveTimeMs(a.creationTime);
  });
}

export function withEffectiveStatus(live: FBLive): FBLive {
  const status = effectiveLiveStatus(live);
  return status === live.status ? live : { ...live, status };
}
